import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, IconButton, TextField, Checkbox, Divider, CircularProgress,
  Switch, Chip, FormControlLabel, Radio, RadioGroup,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import useAtsSenderName from '../../hooks/useAtsSenderName';
import useWhatsappChannel from '../../hooks/useWhatsappChannel';

/**
 * Solicitar documentos al candidato (ATS).
 * Carga el catálogo de documentos del proyecto, permite seleccionar/marcar
 * obligatorios y agregar documentos nuevos (que se guardan en el catálogo del
 * proyecto al enviar). Al enviar, el backend genera el link y manda el correo.
 */
const AtsRequestDocumentsDialog = ({ open, onClose, application, applicantPhone, onSent }) => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert } = useStore.getState();

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [catalog, setCatalog] = useState([]); // [{ id, label, instructions }]
  const [sel, setSel] = useState({});          // { [id]: { required } } — presencia = seleccionado
  const [statusById, setStatusById] = useState({}); // { [id]: 'pending'|'uploaded' }
  const [customDocs, setCustomDocs] = useState([]); // [{ tempId, label, instructions, required }]
  // Canal de envío (2026-08-24): 'email' (link mágico) o 'whatsapp' (plantilla + bot)
  const [channel, setChannel] = useState('email');

  const [newLabel, setNewLabel] = useState('');
  const [newInstructions, setNewInstructions] = useState('');
  const [newRequired, setNewRequired] = useState(false);
  const { senderName, setSenderName } = useAtsSenderName(open);
  // Sin WhatsApp en la organización (sin número propio ni el compartido permitido)
  const whatsapp = useWhatsappChannel(open);
  useEffect(() => {
    if (!whatsapp.usable && channel === 'whatsapp') setChannel('email');
  }, [whatsapp.usable, channel]);

  useEffect(() => {
    if (open) loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadCatalog = async () => {
    setLoading(true);
    setCustomDocs([]);
    setChannel('email');
    setNewLabel('');
    setNewInstructions('');
    setNewRequired(false);
    try {
      const res = await api.get(
        `/projects/document-catalog?organizationId=${organization}&projectId=${project}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const cat = res.data?.response?.data || [];
      const safeCat = Array.isArray(cat) ? cat : [];

      // Pre-seleccionar lo ya solicitado y conservar su estado
      const prev = application?.documentRequest?.items || [];
      const selInit = {};
      const statusInit = {};
      const knownIds = new Set(safeCat.map((c) => c.id));
      const extraCat = [];
      prev.forEach((it) => {
        selInit[it.docId] = { required: !!it.required };
        statusInit[it.docId] = it.status || 'pending';
        if (!knownIds.has(it.docId)) {
          extraCat.push({ id: it.docId, label: it.label, instructions: it.instructions || '' });
          knownIds.add(it.docId);
        }
      });

      setCatalog([...safeCat, ...extraCat]);
      setSel(selInit);
      setStatusById(statusInit);
    } catch (err) {
      console.error(err);
      showAlert(t('ats.documents.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleCatalog = (id) => {
    setSel((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = { required: false };
      return next;
    });
  };

  const setCatalogRequired = (id, required) => {
    setSel((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), required } }));
  };

  const addCustomDoc = () => {
    const label = newLabel.trim();
    if (!label) return;
    setCustomDocs((prev) => [
      ...prev,
      {
        tempId: `tmp-${Date.now()}-${prev.length}`,
        label,
        instructions: newInstructions.trim(),
        required: newRequired,
      },
    ]);
    setNewLabel('');
    setNewInstructions('');
    setNewRequired(false);
  };

  const setCustomRequired = (tempId, required) => {
    setCustomDocs((prev) => prev.map((d) => (d.tempId === tempId ? { ...d, required } : d)));
  };

  const removeCustomDoc = (tempId) => {
    setCustomDocs((prev) => prev.filter((d) => d.tempId !== tempId));
  };

  const totalSelected = Object.keys(sel).length + customDocs.length;
  const phoneText = applicantPhone ? String(applicantPhone).trim() : '';
  const hasPhone = !!phoneText;

  const handleSend = async () => {
    if (totalSelected === 0) {
      showAlert(t('ats.documents.selectAtLeastOne'), 'warning');
      return;
    }
    setSending(true);
    try {
      const documents = [
        ...catalog
          .filter((c) => sel[c.id])
          .map((c) => ({
            id: c.id,
            label: c.label,
            instructions: c.instructions || '',
            required: !!sel[c.id].required,
          })),
        ...customDocs.map((d) => ({
          label: d.label,
          instructions: d.instructions || '',
          required: !!d.required,
        })),
      ];

      const res = await api.post(
        `/applications/${application.uid || application.id}/request-documents`,
        { documents, senderName, channel },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const data = res.data?.response?.data || {};
      if (data.reason === 'NO_PENDING') {
        // Solo quedaron documentos ya subidos: la lista se guardó y no se envió nada
        showAlert(t('ats.documents.nothingPending'), 'info');
      } else if (data.channel === 'whatsapp') {
        showAlert(t('ats.documents.sentWhatsapp'), 'success');
      } else {
        const emailSent = data.emailSent !== false;
        showAlert(
          emailSent ? t('ats.documents.sent') : t('ats.documents.sentNoEmail'),
          emailSent ? 'success' : 'warning'
        );
      }
      if (onSent) onSent(data.items || documents);
      onClose();
    } catch (err) {
      console.error(err);
      // formatResponse: el mensaje del backend viene en response.message / errorMessage
      const backendMsg = err.response?.data?.response?.message || err.response?.data?.errorMessage;
      showAlert(backendMsg || t('ats.documents.sendError'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('ats.documents.dialogTitle')}
        <IconButton onClick={onClose} size="small" disabled={sending}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('ats.documents.dialogHelp')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
              {t('ats.documents.catalogTitle')}
            </Typography>
            {catalog.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('ats.documents.catalogEmpty')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                {catalog.map((c) => {
                  const isSel = !!sel[c.id];
                  return (
                    <Box key={c.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <FormControlLabel
                          sx={{ alignItems: 'flex-start', mr: 0 }}
                          control={<Checkbox checked={isSel} onChange={() => toggleCatalog(c.id)} size="small" />}
                          label={
                            <Box sx={{ pt: 0.5 }}>
                              <Typography variant="body2">{c.label}</Typography>
                              {c.instructions ? (
                                <Typography variant="caption" color="text.secondary">{c.instructions}</Typography>
                              ) : null}
                            </Box>
                          }
                        />
                        {statusById[c.id] === 'uploaded' && (
                          <Chip size="small" color="success" variant="outlined" label={t('ats.documents.uploaded')} />
                        )}
                      </Box>
                      {isSel && (
                        <Box sx={{ pl: 4, pb: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={!!sel[c.id].required}
                                onChange={(e) => setCatalogRequired(c.id, e.target.checked)}
                              />
                            }
                            label={<Typography variant="caption">{t('ats.documents.required')}</Typography>}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {customDocs.length > 0 && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                  {t('ats.documents.newDocsTitle')}
                </Typography>
                {customDocs.map((d) => (
                  <Box
                    key={d.tempId}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      borderBottom: '1px solid', borderColor: 'divider', py: 0.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2">{d.label}</Typography>
                      {d.instructions ? (
                        <Typography variant="caption" color="text.secondary">{d.instructions}</Typography>
                      ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={d.required}
                            onChange={(e) => setCustomRequired(d.tempId, e.target.checked)}
                          />
                        }
                        label={<Typography variant="caption">{t('ats.documents.required')}</Typography>}
                      />
                      <IconButton size="small" onClick={() => removeCustomDoc(d.tempId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              {t('ats.documents.channelTitle')}
            </Typography>
            <RadioGroup
              row
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              sx={{ mb: 0.5 }}
            >
              <FormControlLabel
                value="email"
                control={<Radio size="small" />}
                label={<Typography variant="body2">{t('ats.documents.channelEmail')}</Typography>}
              />
              <FormControlLabel
                value="whatsapp"
                control={<Radio size="small" />}
                disabled={!hasPhone || !whatsapp.usable}
                label={
                  <Typography variant="body2">
                    {t('ats.documents.channelWhatsapp')}
                    {hasPhone ? ` (${phoneText})` : ''}
                  </Typography>
                }
              />
            </RadioGroup>
            {!whatsapp.usable && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('ats.documents.channelWhatsappUnavailable')}
              </Typography>
            )}
            {whatsapp.usable && !hasPhone && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('ats.documents.channelNoPhone')}
              </Typography>
            )}
            {channel === 'whatsapp' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('ats.documents.channelWhatsappHint')}
              </Typography>
            )}
            {channel === 'email' && (
              <TextField
                size="small"
                fullWidth
                label={t('ats.config.emailSender.fieldLabel')}
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                inputProps={{ maxLength: 80 }}
                sx={{ mb: 2 }}
              />
            )}

            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
              {t('ats.documents.addNewTitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                size="small"
                label={t('ats.documents.labelField')}
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                fullWidth
                placeholder={t('ats.documents.labelPlaceholder')}
              />
              <TextField
                size="small"
                label={t('ats.documents.instructionsField')}
                value={newInstructions}
                onChange={(e) => setNewInstructions(e.target.value)}
                fullWidth
              />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={newRequired}
                      onChange={(e) => setNewRequired(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">{t('ats.documents.required')}</Typography>}
                />
                <Button size="small" startIcon={<AddIcon />} onClick={addCustomDoc} disabled={!newLabel.trim()}>
                  {t('ats.documents.addButton')}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {t('ats.documents.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || loading || totalSelected === 0 || (channel === 'whatsapp' && !hasPhone)}
        >
          {sending ? <CircularProgress size={20} /> : t('ats.documents.sendButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsRequestDocumentsDialog;
